<template>
  <div class="sv-filter" :data-test="`sv-filter-${name}`">
    <button
      type="button"
      class="sv-filter__button"
      :class="{ 'sv-filter__button--active': selected.length > 0, 'sv-filter__button--open': open }"
      :disabled="!options.length"
      :aria-expanded="open ? 'true' : 'false'"
      :data-test="`sv-filter-${name}-button`"
      @click="toggle"
    >
      <span class="sv-filter__label">{{ label }}:</span>
      <b class="sv-filter__value">{{ summary }}</b>
      <!--
        A narrowed filter swaps its caret for a ×: one tap puts the whole filter back without opening
        the menu. A <span role="button"> and not a <button>, because it lives INSIDE the filter's own
        button (a button may not contain another); it takes Enter and Space itself.
      -->
      <span
        v-if="selected.length"
        class="sv-filter__x"
        role="button"
        tabindex="0"
        :aria-label="`Clear ${label}`"
        :data-test="`sv-filter-${name}-x`"
        @click.stop="clear"
        @keydown.enter.stop.prevent="clear"
        @keydown.space.stop.prevent="clear"
      ><i class="fa fa-times" aria-hidden="true"></i></span>
      <i v-else class="fa fa-angle-down sv-filter__caret" aria-hidden="true"></i>
    </button>

    <!--
      On a phone the panel is a bottom sheet (see the phone @media block), and a sheet needs a
      backdrop: it dims the schedule so the sheet reads as on top of it, and tapping it closes the
      sheet — the gesture people already use to dismiss one. Hidden by CSS on wider screens.
    -->
    <div
      v-if="open"
      class="sv-filter__backdrop"
      aria-hidden="true"
      :data-test="`sv-filter-${name}-backdrop`"
      @click="open = false"
    ></div>

    <div
      v-if="open"
      class="sv-filter__panel"
      :class="{ 'sv-filter__panel--rich': rich }"
      :data-test="`sv-filter-${name}-panel`"
    >
      <!-- Phone only: the sheet's grab handle, title, Clear and a teal Done (the design's `.fd-head`). -->
      <div class="sv-filter__grab" aria-hidden="true"></div>
      <div class="sv-filter__panel-head">
        <span class="sv-filter__panel-title">{{ label }}</span>
        <button
          v-if="selected.length"
          type="button"
          class="sv-filter__clear"
          :data-test="`sv-filter-${name}-clear`"
          @click="clear"
        >Clear</button>
        <!-- Choices apply as they are ticked; this just puts the sheet away. -->
        <button
          type="button"
          class="sv-filter__done"
          :data-test="`sv-filter-${name}-done`"
          @click="open = false"
        >Done</button>
      </div>

      <ul class="sv-filter__list">
        <li v-for="opt in options" :key="opt.value">
          <label
            class="sv-filter__option"
            :class="{ 'sv-filter__option--on': isSelected(opt.value), 'sv-filter__option--zero': hasCount(opt) && !opt.count }"
          >
            <input
              type="checkbox"
              class="sv-filter__check"
              :checked="isSelected(opt.value)"
              :data-test="`sv-filter-${name}-option-${opt.value}`"
              @change="toggleOption(opt.value)"
            />
            <span v-if="opt.icon" class="sv-filter__tile" aria-hidden="true">
              <i
                class="fa sv-filter__option-icon"
                :class="opt.icon"
                :data-test="`sv-filter-${name}-icon-${opt.value}`"
              ></i>
            </span>
            <span class="sv-filter__option-text">{{ opt.label }}</span>
            <span v-if="hasCount(opt) || opt.total" class="sv-filter__option-end">
              <span
                v-if="opt.total"
                class="sv-filter__option-total"
                :data-test="`sv-filter-${name}-total-${opt.value}`"
              >{{ opt.total }}</span>
              <span
                v-if="hasCount(opt)"
                class="sv-filter__option-count"
                :class="{ 'sv-filter__option-count--zero': !opt.count }"
                :data-test="`sv-filter-${name}-count-${opt.value}`"
              >{{ opt.count }}</span>
            </span>
          </label>
        </li>
      </ul>

      <!-- Desktop / iPad: the menu's last row. (A phone's Clear is in the sheet's head.) -->
      <div v-if="selected.length" class="sv-filter__clear-row">
        <button
          type="button"
          class="sv-filter__clear-btn"
          :data-test="`sv-filter-${name}-clear-row`"
          @click="clear(); open = false"
        >Clear {{ label.toLowerCase() }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * One narrowing control — Provider, Operatory, Status or Alerts.
 *
 * An option is { value, label } and may also carry (all optional, WP-A5 2026-10-23): `count` (a
 * number, a pill on the right — zero is a real count and is drawn), `total` (a ready-made money
 * string, beside the count) and `icon` (a font-awesome class, before the label — the alert's own
 * icon, the one it has on a block). Options with none of them (Provider, Operatory) draw as a check
 * and a label. The component formats nothing: the page decides what the numbers mean.
 *
 * Empty selection means "show everything", never "show nothing" (see utils/scheduleView/filters.js
 * for why). The button says which — "Provider: All" — and says WHAT when it is narrowed: the one
 * option's own name when one is ticked, "N selected" otherwise, so a narrowed schedule can never be
 * mistaken for a quiet day from the toolbar alone.
 *
 * Emits `change` with the new array and owns no state but whether it is open — the filters live in
 * the store so they survive a date change and are remembered per browser.
 *
 * The look is the Forms Settings one (WP-C, 2026-10-23): a 38px button, teal when narrowed, a
 * 3px teal ring while open; a 14px-radius menu with a "Clear <label>" row at the bottom; and on a
 * phone a bottom sheet with a grab handle, the title, Clear and a teal Done.
 */
export default {
  name: 'ScheduleFilterDropdown',

  props: {
    /** Used for the data-test hooks: 'provider' | 'operatory' | 'status' | 'alert'. */
    name: { type: String, required: true },
    label: { type: String, required: true },
    /** [{ value, label, count?, total?, icon? }] — already narrowed to what is actually on the day. */
    options: { type: Array, default: () => [] },
    /** The selected values. Empty = no filter. */
    selected: { type: Array, default: () => [] },
  },

  data() {
    return { open: false };
  },

  computed: {
    /** Rows with icons carry long alert names: the panel gets room for them (300px+, never 250). */
    rich() {
      return this.options.some(o => !!o.icon);
    },

    summary() {
      if (!this.options.length) return 'None today';
      if (!this.selected.length) return 'All';
      if (this.selected.length === 1) {
        const hit = this.options.find(o => String(o.value) === String(this.selected[0]));
        return hit ? hit.label : '1 selected';
      }
      return `${this.selected.length} selected`;
    },
  },

  // A dropdown that only closes on its own button traps people behind it on a tablet, where there
  // is no Escape key and the next thing they tap is the schedule itself.
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    toggle() {
      this.open = !this.open;
    },

    hasCount(opt) {
      return typeof opt.count === 'number' && Number.isFinite(opt.count);
    },

    isSelected(value) {
      return this.selected.some(v => String(v) === String(value));
    },

    toggleOption(value) {
      const next = this.isSelected(value)
        ? this.selected.filter(v => String(v) !== String(value))
        : this.selected.concat([value]);
      this.$emit('change', next);
    },

    clear() {
      this.$emit('change', []);
    },

    onDocumentClick(event) {
      if (!this.open) return;
      if (this.$el && this.$el.contains(event.target)) return;
      this.open = false;
    },

    onKeydown(event) {
      if (event.key === 'Escape' && this.open) this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-filter {
  position: relative;
}

// "Provider: All" — 38px, 10px radius, the soft shadow. The label is muted and the value bold.
.sv-filter__button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 38px;
  max-width: 260px;
  padding: 0 12px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  box-shadow: $sv-shadow-1;
  color: $sv-ink-3;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) { background: $sv-bg; border-color: $sv-line-strong; }
  &:disabled { opacity: 0.5; cursor: default; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: 2px; }

  // Open: a teal border and a 3px teal-subtle ring.
  &--open,
  &--open:hover:not(:disabled) {
    border-color: $sv-brand;
    box-shadow: 0 0 0 3px $sv-brand-subtle;
    background: $sv-surface;
  }

  // Narrowed: a teal-subtle fill, a teal edge, deep-teal text — and a × in place of the caret.
  &--active,
  &--active:hover:not(:disabled) {
    background: $sv-brand-subtle;
    border-color: $sv-brand-edge;
    color: $sv-brand-deep;
  }

  &--active.sv-filter__button--open { border-color: $sv-brand; }
}

.sv-filter__label {
  flex: 0 0 auto;
}

.sv-filter__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $sv-ink;
  font-weight: 700;
}

.sv-filter__button--active .sv-filter__value { color: $sv-brand-deep; }

.sv-filter__caret {
  flex: 0 0 auto;
  margin-left: 3px;
  color: $sv-ink-3;
  font-size: 14px;
}

.sv-filter__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-left: 4px;
  border-radius: 50%;
  color: $sv-brand-deep;
  font-size: 11px;
  cursor: pointer;

  &:hover { background: rgba(15, 114, 89, 0.12); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: 1px; }
}

// The menu: 14px radius, the raised shadow, as wide as its longest row (never under 250px; the
// Alerts menu never under 300px — its row names are long, and at 250px "Unscheduled treatment
// pending" wrapped onto three lines in the design).
.sv-filter__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  box-sizing: border-box;
  width: max-content;
  min-width: 250px;
  max-width: 380px;
  max-height: min(70vh, 520px);
  overflow-y: auto;
  padding: 6px;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-radius: $sv-radius-lg;
  box-shadow: $sv-shadow-3;
}

.sv-filter__panel--rich {
  min-width: 300px;
}

.sv-filter__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

// check · icon · label · ($ total) · count pill. A 36px row is a comfortable tap target on the
// operatory iPad; 24px is not.
.sv-filter__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: $sv-ink;
  cursor: pointer;

  &:hover { background: $sv-bg; }
}

// The box: the native checkbox itself, drawn flat (so a keyboard, a screen reader and a test all see
// the real control). A white tick in a teal square when it is on.
.sv-filter__check {
  flex: 0 0 auto;
  width: 17px;
  height: 17px;
  margin: 0;
  border: 1.5px solid $sv-line-strong;
  border-radius: 5px;
  background: $sv-surface no-repeat center / 11px 11px;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;

  &:checked {
    border-color: $sv-brand;
    background-color: $sv-brand;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 6.4l2.4 2.4 4.6-5.2' fill='none' stroke='%23ffffff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  }

  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: 2px; }
}

// The alert's icon, in a quiet tile — monochrome like the icon on the block (colour is the status
// dot's and the stripe's job on this page, not the alerts').
.sv-filter__tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: $sv-bg-3;
  color: $sv-ink-2;
}

.sv-filter__option-icon {
  font-size: 12px;
}

.sv-filter__option-text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.25;
}

.sv-filter__option-end {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: 8px;
}

.sv-filter__option-total {
  font-size: 12px;
  color: $sv-ink-3;
  font-variant-numeric: tabular-nums;
}

// A pill, tabular so a column of counts lines up. The ticked row's pill turns teal.
.sv-filter__option-count {
  min-width: 22px;
  padding: 1px 7px;
  border-radius: $sv-radius-pill;
  background: $sv-bg-3;
  color: $sv-ink-2;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sv-filter__option--on .sv-filter__option-count {
  background: $sv-brand;
  color: #ffffff;
}

// A row that would add nothing: its words and icon dim to half. The 0 itself is still drawn — it is
// an answer — and a dimmed row is still tickable.
.sv-filter__option--zero .sv-filter__option-text,
.sv-filter__option--zero .sv-filter__tile {
  opacity: 0.5;
}

.sv-filter__clear-row {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid $sv-line-2;
}

.sv-filter__clear-btn {
  display: block;
  width: 100%;
  min-height: 36px;
  padding: 6px 10px 6px 37px;
  border: 0;
  border-radius: 9px;
  background: none;
  color: $sv-ink-3;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover { background: $sv-bg; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: -2px; }
}

// Phone-only furniture: the backdrop, the grab handle and the sheet's head.
.sv-filter__backdrop,
.sv-filter__grab,
.sv-filter__panel-head {
  display: none;
}

/**
 * Phone: a bottom sheet, not a dropdown.
 *
 * A 250px panel hung off a button near the right of a 390px screen runs off the glass, and the
 * rows are too small for a thumb. A sheet is full width, sits where the thumb already is, and has
 * room for 48px rows. The design's `.fd-sheet`: a grab handle, the title, Clear, a teal Done.
 *
 * z-index 10000+: above the AI agent's floating button (agent-panel.css, 9999 !important), which
 * otherwise sits on top of the sheet's lower rows on a phone. A sheet is modal; it covers it.
 */
@media (max-width: $sv-phone-max) {
  .sv-filter__backdrop {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10000;
    background: rgba(12, 15, 14, 0.32);
  }

  .sv-filter__panel {
    position: fixed;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10001;
    display: flex;
    flex-direction: column;
    width: auto;
    min-width: 0;
    max-width: none;
    max-height: 78vh;
    padding: 0 0 env(safe-area-inset-bottom);
    border: 0;
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -12px 40px -16px rgba(12, 15, 14, 0.3);
    // A scroll that hits the end of the list must not carry on scrolling the schedule behind it.
    overscroll-behavior: contain;
    overflow: hidden;
  }

  .sv-filter__panel--rich { min-width: 0; }

  .sv-filter__grab {
    display: block;
    flex: 0 0 auto;
    width: 38px;
    height: 4px;
    margin: 9px auto 2px;
    border-radius: 99px;
    background: $sv-line-strong;
  }

  .sv-filter__panel-head {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 8px;
    padding: 8px 16px 10px;
    border-bottom: 1px solid $sv-line-2;
  }

  .sv-filter__panel-title {
    flex: 1 1 auto;
    color: $sv-ink;
    font-size: 16px;
    font-weight: 700;
  }

  .sv-filter__clear {
    height: 38px;
    padding: 0 12px;
    border: 0;
    background: none;
    color: $sv-ink-3;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .sv-filter__done {
    height: 38px;
    padding: 0 16px;
    border: 0;
    border-radius: $sv-radius;
    background: $sv-brand;
    color: #ffffff;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }

  .sv-filter__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 6px 8px 22px;
  }

  .sv-filter__option {
    min-height: 48px;
    font-size: 14.5px;
  }

  .sv-filter__check {
    width: 20px;
    height: 20px;
  }

  // The sheet's Clear lives in its head; the menu's last row is for the other layouts.
  .sv-filter__clear-row { display: none; }
}
</style>
